import type { VideoPermission } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import { VIDEO_TOOLTIPS } from 'Modules/softphone/constants/callActionCopy';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { isConferenceCallSelector } from 'Modules/softphone/store/slices/callSlice/index.selectors';
import { useContext, useMemo } from 'react';
import sdpService from 'Services/voipSdpService';
import type { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Hook that determines whether video functionality is enabled or disabled
 * for the current call, based on user permissions and contact type.
 */
export const useVideoPermission = (): VideoPermission => {
  const rootStore = useContext(MobXProviderContext) as RootStoreProps | null;

  const { currentCall, isConferenceCall } = useSoftphoneStore((state) => ({
    currentCall: state.currentCallId
      ? state.activeCalls.get(state.currentCallId) ?? null
      : null,
    isConferenceCall: isConferenceCallSelector(state),
  }));

  const isVideoFeatureEnabled =
    rootStore?.person?.personAvaliableFeatures?.video?.enabled ?? false;

  return useMemo((): VideoPermission => {
    // No Pro package
    if (!isVideoFeatureEnabled) {
      return {
        isVideoDisabled: true,
        videoTooltip: VIDEO_TOOLTIPS.noProPackage,
        disabledReason: 'no-pro-package',
      };
    }

    // No active call
    if (!currentCall || currentCall.state !== 'active') {
      return {
        isVideoDisabled: true,
        videoTooltip: undefined,
        disabledReason: 'no-active-call',
      };
    }

    // Merged/conference call detection (equivalent to legacy isMerge)
    if (isConferenceCall) {
      return {
        isVideoDisabled: true,
        videoTooltip: VIDEO_TOOLTIPS.conferenceCall,
        disabledReason: 'conference-call',
      };
    }

    // External contact detection
    if (!currentCall.personId) {
      const isExternal = sdpService.analyzeIfCallIsExternal(
        undefined,
        currentCall.phoneNumber
      );

      if (isExternal) {
        return {
          isVideoDisabled: true,
          videoTooltip: VIDEO_TOOLTIPS.externalContact,
          disabledReason: 'external-contact',
        };
      }
    }

    // Enabled
    return {
      isVideoDisabled: false,
      videoTooltip: VIDEO_TOOLTIPS.enabled,
      disabledReason: null,
    };
  }, [isVideoFeatureEnabled, isConferenceCall, currentCall]);
};
